import endpoints from '../constants/endpoints';
import { getAppObj, safe, serialize } from '../utils/utils';
import { getErrorStatus } from '../helpers/formatters';
import { brandIdSelector } from '../helpers/selectors';
import { setAppLoaderState } from './brand';
import {
  clearLoginPending,
  clearSentToKeycloakLogin,
  setLoginPending,
  setLogoutPending
} from '../utils/keycloak';
import { logout } from './user';
import { clearAuthTokens, clearSsoTokens, getAppSsoTokens } from '../utils/tokens';
import { STORAGE_KEY } from '../constants/generic';

export function verifyUser(updateIncompleteStatus = true) {
  return (dispatch) => {
    console.log('[KC-DEBUG] actions/keycloak.js/verifyUser: FIRING HTTP GET', { url: endpoints.me, updateIncompleteStatus }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] actions/keycloak.js/verifyUser: ENTER - timestamped; reaching this line at all means kc.init() resolved AUTH (it is the ONLY caller)', { atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), url: endpoints.me, hasSsoToken: Boolean(getAppSsoTokens().token), ssoTokenLength: getAppSsoTokens().token?.length, hasSsoRefreshToken: Boolean(getAppSsoTokens().refreshToken), kcAuthenticated: getAppObj().keycloak?.authenticated }); // eslint-disable-line no-console
    return dispatch({
      type: 'keycloak/VERIFY_USER',
      updateIncompleteStatus,
      payload: {
        request: {
          method: 'get',
          url: endpoints.me
        }
      }
    });
  };
}

// noinspection JSUnusedGlobalSymbols
export function handleVerifyUserFail(payload) {
  console.log('[KC-DEBUG] actions/keycloak.js/handleVerifyUserFail: dispatching keycloak/VERIFY_FAILED (the reducer branches on the HTTP status from here)', { httpStatus: getErrorStatus(payload), respType: payload?.type, atIso: new Date().toISOString() }); // eslint-disable-line no-console
  return {
    type: 'keycloak/VERIFY_FAILED',
    payload
  };
}

// The outcome of kc.init(), one of KEYCLOAK_STATUS, as reported by the init callback.
// Anything that has to tell "Keycloak says there is no session" from "Keycloak has not
// answered" needs this: the app loader only says the app stopped waiting.
export function setKeycloakInitStatus(payload) {
  console.log('[KC-DEBUG] actions/keycloak.js/setKeycloakInitStatus: dispatching keycloak/SET_INIT_STATUS', { payload, statusName: { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[payload] }); // eslint-disable-line no-console
  return {
    type: 'keycloak/SET_INIT_STATUS',
    payload
  };
}

export function getRemoteKeycloakConfig(email) {
  return (dispatch, getState) => {
    const params = { brandId: brandIdSelector(getState()), email };

    console.log('[KC-DEBUG] actions/keycloak.js/getRemoteKeycloakConfig: FIRING HTTP GET', { url: '/v2/companies/sso-registration', brandId: params.brandId, hasEmail: Boolean(email) }); // eslint-disable-line no-console
    return dispatch({
      type: 'keycloak/GET_REMOTE_CONFIG',
      payload: {
        request: {
          method: 'get',
          url: `/v2/companies/sso-registration?${serialize(params)}`
        }
      }
    });
  };
}

export function handleKeycloakLogin(keycloakConfig) {
  return (dispatch) => {
    const { keycloak } = getAppObj();
    const { createLoginUrl } = keycloak || {};
    const { identityProvider } = keycloakConfig || {};

    console.log('[KC-DEBUG] actions/keycloak.js/handleKeycloakLogin: DECISION `if (createLoginUrl)` (falsy => nothing happens at all, user never leaves for KC)', { hasKcClient: Boolean(keycloak), hasCreateLoginUrl: Boolean(createLoginUrl), identityProvider, hasIdentityProvider: Boolean(identityProvider) }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] actions/keycloak.js/handleKeycloakLogin: ENTER - timestamped; kc client state at the moment the user clicks Login', { atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), kcDidInitialize: keycloak?.didInitialize, kcAuthenticated: keycloak?.authenticated, kcRealm: keycloak?.realm, kcAuthServerUrl: keycloak?.authServerUrl, keycloakConfigKeys: keycloakConfig ? Object.keys(keycloakConfig).join(',') : null, keycloakConfigJson: safe(() => JSON.stringify(keycloakConfig)) }); // eslint-disable-line no-console
    if (!createLoginUrl) return Promise.resolve(false);

    dispatch(setAppLoaderState(true));
    setLoginPending();

    // The whole call is wrapped so that no shape of failure can strand the loader on
    // an app that is going nowhere: createLoginUrl returns a string in the installed
    // keycloak-js 25.x and a promise in the 26.x the manifest asks for (TODO.md §3),
    // and it throws outright when `endpoints` is unset - and a throw escaping a
    // caller's useEffect unmounts the React root, i.e. a white page.
    // Resolves to true only once the visitor is actually on his way to Keycloak -
    // callers that need to know he left can rely on that.
    return Promise.resolve()
      .then(() => createLoginUrl({ prompt: 'login' }))
      .then((url) => {
        if (!url) throw new Error('Keycloak produced no login URL');

        console.log('[KC-DEBUG] actions/keycloak.js/handleKeycloakLogin: redirecting to KC login URL (query stripped)', { urlWithoutQuery: String(url).split('?')[0], hasIdpHintParam: String(url).includes('kc_idp_hint') }); // eslint-disable-line no-console
        window.location.href = url;

        return true;
      })
      .catch((e) => {
        console.log('[KC-DEBUG] actions/keycloak.js/handleKeycloakLogin: FAILED to start the KC login - the user stays here, loader released, LOGIN_PENDING cleared', { errorMessage: e?.message, errorName: e?.name, errorString: String(e) }); // eslint-disable-line no-console
        dispatch(setAppLoaderState(false));
        clearLoginPending();

        return false;
      });
  };
}

export function handleKeycloakLogout() {
  return (dispatch) => {
    const { keycloak } = getAppObj();
    const hasKeycloakSession = Boolean(keycloak && keycloak.authenticated);

    // Whatever the registration guard sent this tab through is finished either way:
    // the tab survives both logout paths, so leaving the marker set would block the
    // next registration started from it.
    clearSentToKeycloakLogin();

    console.log('[KC-DEBUG] actions/keycloak.js/handleKeycloakLogout: DECISION - KC logout vs local logout', { hasKeycloakSession, hasLogoutFn: Boolean(keycloak?.logout), usesKcLogout: Boolean(hasKeycloakSession && keycloak.logout) }); // eslint-disable-line no-console
    console.log('[KC-DEBUG] actions/keycloak.js/handleKeycloakLogout: ENTER - timestamped; `authenticated` false here means the app falls back to a LOCAL logout and the KC session survives', { atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), kcDidInitialize: keycloak?.didInitialize, kcAuthenticated: keycloak?.authenticated, redirectUri: window.location.origin }); // eslint-disable-line no-console
    if (hasKeycloakSession && keycloak.logout) {
      dispatch(setAppLoaderState(true));
      clearAuthTokens();
      clearSsoTokens();
      sessionStorage.removeItem(STORAGE_KEY.USER);
      setLogoutPending();
      keycloak.logout({ redirectUri: window.location.origin });
    } else {
      dispatch(logout(true));
    }
  };
}
